import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import MainLayout from './components/layout/MainLayout'
import CreateTask from './components/tasks/CreateTask'
import Calendar from './components/calendar/Calendar'
import Tasks from './components/tasks/Tasks'
import Notifications from './components/notifications/Notifications'
import Team from './components/team/Team'
import Files from './components/files/Files'
import Projects from './components/projects/Projects'
import Consulting from './components/consulting/Consulting'
import Chat from './components/chat/Chat'
import SignIn from './app/signin/page'
import { BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts'
import { ResponsiveContainer } from 'recharts'
import DashboardSkeleton from './components/dashboard/DashboardSkeleton'
import RegisterForm from './components/auth/RegisterForm'
import LoginForm from './components/auth/LoginForm'
import ForgotPassword from './components/auth/ForgotPassword'
import ResetPassword from './components/auth/ResetPassword'
import EmailVerification from './components/auth/EmailVerification'
import { ThemeProvider } from './context/ThemeContext'
import { AuthProvider } from './hooks/useAuth.jsx'
import { createClient } from '@supabase/supabase-js'
import { useNavigate } from 'react-router-dom'
import ProfileInfo from './pages/ProfileInfo'
import AccountSettings from './pages/AccountSettings'
import NotificationPreferences from './pages/notification-preferences'
import SecurityPrivacy from './pages/SecurityPrivacy'
import ThemePreferences from './pages/theme-preferences'
import HelpSupport from './pages/HelpSupport'
import Activities from './components/activities/Activities'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'
// Landing Page komponentini import et
import LandingPage from './components/pages/LandingPage'
// Contact Page komponentini import et
import Contact from './components/pages/Contact'
// Not Found page komponentini import et
import NotFound from './components/pages/NotFound'
// Test NotFound component for development
import TestNotFound from './components/pages/TestNotFound'
// Smooth scroll fonksiyonlarını içe aktar
import { initSmoothScrolling } from './utils/smoothScroll'
import { initScrollHelpers } from './utils/scrollHelper'
import TestScroll from './components/pages/TestScroll'
// Stok Takip sayfası için import
import StokFlow from './pages/StokFlow'
import StokProductCreate from './pages/StokProductCreate'
import StokLayout from './components/stokflow/StokLayout'
import StokYonetimi from './pages/StokYonetimi'
import StokProductEdit from './pages/StokProductEdit'
import Transactions from './pages/Transactions'  // Add this import
import MakineMontajListesi from './pages/MakineMontajListesi'; // Makine Montaj Listesi import edildi
// Import the new page components
import Documentation from './components/pages/Documentation';
import Tutorials from './components/pages/Tutorials';
// Import the Blog component
import Blog from './components/pages/Blog';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsOfService from './components/pages/TermsOfService';
import KVKK from './components/pages/KVKK';
import Demo from './pages/Demo';
import CookieConsent from './components/common/CookieConsent';
// Import LoadingFallback component
import LoadingFallback from './components/common/LoadingFallback';
// Import debug utilities
import { loadingDebugger } from './utils/debugUtils';
// Import DataIntegrityCheck component
import DataIntegrityCheck from './components/admin/DataIntegrityCheck';
// Bildirim sesi servislerini import et
import { playNotificationSound, updateNotificationSoundSettings } from './lib/notificationService';

// Sistem mod koruyucu wrapper bileşeni
const PanelModePreserver = ({ component: Component }) => {
  useEffect(() => {
    // Mevcut activePanel değerini korur
    const currentMode = localStorage.getItem('activePanel');
    if (currentMode) {
      // Sayfa yüklenirken mevcut modu korumak için yeniden atanır
      localStorage.setItem('activePanel', currentMode);
    }
  }, []);

  return <Component />;
};

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
)

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const location = useLocation(); // Get current location

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Timeout ekle (8 saniye)
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Auth check timeout')), 8000)
        );
        
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);
        
        if (error) throw error;
        
        if (!session) {
          // Oturum yoksa tüm oturum verilerini temizle
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('userProfile');
          localStorage.removeItem('activePanel');
          navigate('/signin');
          return;
        }

        // Kullanıcının yetki kontrolü
        const activePanel = localStorage.getItem('activePanel');
        
        if (activePanel) {
          // Timeout ile permission check
          const permissionPromise = supabase
            .from('user_profiles')
            .select('is_takip_permission, stok_takip_permission')
            .eq('id', session.user.id)
            .single();
            
          const permissionTimeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Permission check timeout')), 5000)
          );
          
          try {
            const { data: userProfile, error: profileError } = await Promise.race([
              permissionPromise,
              permissionTimeoutPromise
            ]);
            
            if (profileError) {
              console.warn('Permission check error:', profileError);
              // Permission check başarısız olursa varsayılan olarak izin ver ama dikkatli ol
              setHasPermission(true);
            } else {
              // Aktif panele göre yetki kontrolü
              if (activePanel === 'job' && userProfile.is_takip_permission === false) {
                setHasPermission(false);
                navigate('/signin');
                return;
              }
              
              if (activePanel === 'stock' && userProfile.stok_takip_permission === false) {
                setHasPermission(false);
                navigate('/signin');
                return;
              }
              
              setHasPermission(true);
            }
          } catch (permissionError) {
            console.warn('Permission check timeout or error:', permissionError);
            // Timeout durumunda varsayılan olarak izin ver
            setHasPermission(true);
          }
        } else {
          setHasPermission(true);
        }
        
      } catch (error) {
        console.error('Auth check error:', error);
        // Hata durumunda login'e yönlendir
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userProfile');
        localStorage.removeItem('activePanel');
        navigate('/signin');
      } finally {
        // Her durumda loading'i false yap
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <LoadingFallback 
        message="Kimlik doğrulanıyor..."
        timeout={12000}
        onTimeout={(isRetry) => {
          if (isRetry) {
            // Store current location before retry
            localStorage.setItem('lastLocation', location.pathname + location.search);
            
            // Retry durumunda auth check'i yeniden çalıştır
            setIsLoading(true);
            const checkAuth = async () => {
              try {
                // Timeout ekle (8 saniye)
                const sessionPromise = supabase.auth.getSession();
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('Auth check timeout')), 8000)
                );
                
                const { data: { session }, error } = await Promise.race([
                  sessionPromise,
                  timeoutPromise
                ]);
                
                if (error) throw error;
                
                if (!session) {
                  // Oturum yoksa tüm oturum verilerini temizle
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  localStorage.removeItem('userProfile');
                  localStorage.removeItem('activePanel');
                  navigate('/signin');
                  return;
                }

                // Kullanıcının yetki kontrolü
                const activePanel = localStorage.getItem('activePanel');
                
                if (activePanel) {
                  // Timeout ile permission check
                  const permissionPromise = supabase
                    .from('user_profiles')
                    .select('is_takip_permission, stok_takip_permission')
                    .eq('id', session.user.id)
                    .single();
                    
                  const permissionTimeoutPromise = new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Permission check timeout')), 5000)
                  );
                  
                  try {
                    const { data: userProfile, error: profileError } = await Promise.race([
                      permissionPromise,
                      permissionTimeoutPromise
                    ]);
                    
                    if (profileError) {
                      console.warn('Permission check error:', profileError);
                      // Permission check başarısız olursa varsayılan olarak izin ver ama dikkatli ol
                      setHasPermission(true);
                    } else {
                      // Aktif panele göre yetki kontrolü
                      if (activePanel === 'job' && userProfile.is_takip_permission === false) {
                        setHasPermission(false);
                        navigate('/signin');
                        return;
                      }
                      
                      if (activePanel === 'stock' && userProfile.stok_takip_permission === false) {
                        setHasPermission(false);
                        navigate('/signin');
                        return;
                      }
                      
                      setHasPermission(true);
                    }
                  } catch (permissionError) {
                    console.warn('Permission check timeout or error:', permissionError);
                    // Timeout durumunda varsayılan olarak izin ver
                    setHasPermission(true);
                  }
                } else {
                  setHasPermission(true);
                }
                
              } catch (error) {
                console.error('Auth check error:', error);
                // Hata durumunda login'e yönlendir
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('userProfile');
                localStorage.removeItem('activePanel');
                navigate('/signin');
              } finally {
                // Her durumda loading'i false yap
                setIsLoading(false);
              }
            };
            checkAuth();
          }
        }}
      />
    );
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
          Erişim Yetkisi Yok
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
          Bu sisteme erişim yetkiniz bulunmamaktadır. Lütfen yöneticinizle iletişime geçin.
        </p>
        <button 
          onClick={() => navigate('/signin')}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Giriş Sayfasına Dön
        </button>
      </div>
    );
  }

  return children;
};

// Animasyonlu sayı komponenti
const AnimatedNumber = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime;
    const startValue = displayValue;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setDisplayValue(Math.floor(startValue + (value - startValue) * progress));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, duration]);

  return displayValue;
};

// CSS Animasyonları için style elementi
const animationStyles = `
  @keyframes slideInUp {
    0% {
      transform: translateY(50px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes scaleIn {
    0% {
      transform: scale(0.5);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes rotateIn {
    0% {
      transform: rotate(-180deg) scale(0.3);
      opacity: 0;
    }
    100% {
      transform: rotate(0) scale(1);
      opacity: 1;
    }
  }

  .animate-mount {
    opacity: 0;
    transform: translateY(50px);
  }

  .dashboard-card:nth-child(1) { animation-delay: 0.1s; }
  .dashboard-card:nth-child(2) { animation-delay: 0.2s; }
  .dashboard-card:nth-child(3) { animation-delay: 0.3s; }
  .dashboard-card:nth-child(4) { animation-delay: 0.4s; }

  .chart-card {
    animation: scaleIn 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    opacity: 0;
    transform: scale(0.5);
  }

  .chart-card:nth-child(1) { animation-delay: 0.5s; }
  .chart-card:nth-child(2) { animation-delay: 0.6s; }

  .pie-container {
    animation: rotateIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    opacity: 0;
    transform-origin: center center;
  }

  .legend-item {
    animation: slideInUp 0.4s cubic-bezier(0.23, 1, 0.32, 1) forwards;
    opacity: 0;
    transform: translateY(20px);
  }

  .legend-item:nth-child(1) { animation-delay: 0.7s; }
  .legend-item:nth-child(2) { animation-delay: 0.8s; }
  .legend-item:nth-child(3) { animation-delay: 0.9s; }
  .legend-item:nth-child(4) { animation-delay: 1.0s; }

  .stat-card {
    animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    opacity: 0;
    transform: scale(0.5);
  }

  .stat-card:nth-child(1) { animation-delay: 1.1s; }
  .stat-card:nth-child(2) { animation-delay: 1.2s; }

  /* Animasyonları tekrar oynatmak için */
  .animate-mount {
    animation: none;
    opacity: 0;
  }

  .animate-mount.show {
    animation: slideInUp 0.6s cubic-bezier(0.23, 1, 0.32, 1) forwards;
  }
`;

// Sayfa bileşenleri
const Login = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Hesabınıza giriş yapın
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            veya{' '}
            <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
              yeni hesap oluşturun
            </a>
          </p>
        </div>
        <form className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="E-posta adresi"
              />
            </div>
            <div>
              <input
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Şifre"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Beni hatırla
              </label>
            </div>

            <div className="text-sm">
              <a href="#" className="font-medium text-primary-600 hover:text-primary-500">
                Şifremi unuttum
              </a>
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Giriş Yap
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                  Veya şununla devam et
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div>
                <a
                  href="#"
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="sr-only">Google ile giriş yap</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z" />
                  </svg>
                </a>
              </div>

              <div>
                <a
                  href="#"
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span className="sr-only">Microsoft ile giriş yap</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState('6'); // Yeni state
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    activeUsers: 0,
    taskStats: {
      totalChange: 0,
      completedChange: 0,
      pendingChange: 0,
      usersChange: 0
    }
  });
  const [weeklyStats, setWeeklyStats] = useState([]);
  const [taskStatusStats, setTaskStatusStats] = useState({
    pieData: [],
    completionRate: 0,
    totalTasks: 0
  });
  const [activities, setActivities] = useState([]);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]); // Tüm zaman aralığındaki görevler

  // Aktiviteleri çekmek için fonksiyon
  const fetchActivities = async () => {
    try {
      const { data: activitiesData, error } = await supabase
        .from('activities')
        .select(`
          *,
          user_profiles!activities_user_id_fkey (
            first_name,
            last_name,
            profile_photo_url,
            job_position
          ),
          tasks!activities_task_id_fkey(
            id,
            title,
            priority
          )
        `)
        .order('created_at', { ascending: false })
        .limit(4);

      if (error) throw error;

      const formattedActivities = activitiesData.map(activity => ({
        id: activity.id,
        user: {
          name: activity.user_profiles ? 
            `${activity.user_profiles.first_name} ${activity.user_profiles.last_name}` : 
            'Anonim Kullanıcı',
          avatar: activity.user_profiles?.profile_photo_url,
          position: activity.user_profiles?.job_position
        },
        type: activity.activity_type,
        description: activity.description,
        taskTitle: activity.tasks?.title,
        taskPriority: activity.tasks?.priority,
        task_id: activity.tasks?.id,
        createdAt: new Date(activity.created_at)
      }));

      setActivities(formattedActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  // Görev durumunu hesaplama
  const getTaskStatus = (startDate, endDate) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Toplam süre (ms)
    const totalDuration = end - start;
    // Geçen süre (ms)
    const elapsedTime = now - start;
    // İlerleme yüzdesi
    const progress = Math.max(0, Math.min(100, (elapsedTime / totalDuration) * 100));

    // Görev henüz başlamamış
    if (now < start) {
      return {
        status: 'upcoming',
        color: 'blue',
        progress: 0,
        message: `${formatDistanceToNow(start, { addSuffix: true, locale: tr })} başlayacak`
      };
    }
    
    // Görev süresi dolmuş
    if (now > end) {
      return {
        status: 'overdue',
        color: 'red',
        progress: 100,
        message: `${formatDistanceToNow(end, { addSuffix: true, locale: tr })} bitti`
      };
    }

    // Görev devam ediyor
    let color = 'yellow';
    if (progress < 30) color = 'green';
    else if (progress > 70) color = 'red';

    return {
      status: 'in_progress',
      color,
      progress: Math.round(progress),
      message: `%${Math.round(progress)} tamamlandı, ${formatDistanceToNow(end, { addSuffix: true, locale: tr })} bitecek`
    };
  };

  // Yaklaşan görevleri getir
  const fetchUpcomingTasks = async () => {
    try {
      const today = new Date();
      const nextWeek = new Date();
      nextWeek.setDate(today.getDate() + 7);

      const { data: tasks, error } = await supabase
        .from('tasks')
        .select(`
          *,
          project:projects(name),
          assigned_members
        `)
        .not('status', 'in', '("done","cancelled")')
        .order('end_date', { ascending: true })
        .limit(3);

      if (error) throw error;

      // Görevleri işle ve durumlarını hesapla
      const processedTasks = tasks.map(task => ({
        ...task,
        status: getTaskStatus(task.start_date, task.end_date)
      }));

      // Öncelik sırası: Geciken > Devam Eden > Yaklaşan
      const sortedTasks = processedTasks.sort((a, b) => {
        const statusOrder = { overdue: 0, in_progress: 1, upcoming: 2 };
        return statusOrder[a.status.status] - statusOrder[b.status.status];
      });

      setUpcomingTasks(sortedTasks);
    } catch (error) {
      console.error('Error fetching upcoming tasks:', error);
    }
  };

  // İlk yükleme ve realtime updates
  useEffect(() => {
    fetchActivities();
    fetchUpcomingTasks();
    fetchDashboardStats(selectedTimeRange);

    // Realtime subscription for tasks
    const tasksSubscription = supabase
      .channel('tasks_changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'tasks' }, 
        () => {
          fetchUpcomingTasks();
          fetchDashboardStats(selectedTimeRange);
        }
      )
      .subscribe();

    return () => {
      tasksSubscription.unsubscribe();
    };
  }, [selectedTimeRange]);

  // Görev önceliğine göre renk belirleme
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Kalan süreyi hesaplama
  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Bugün';
    if (days === 1) return 'Yarın';
    return `${days} gün kaldı`;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchDashboardStats(),
          fetchWeeklyStats(selectedTimeRange),
          fetchTaskStatusStats(),
          fetchActivities()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Gerçek zamanlı güncellemeler için subscription
    const activitySubscription = supabase
      .channel('activities')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'activities'
      }, () => {
        fetchActivities();
      })
      .subscribe();

    return () => {
      activitySubscription.unsubscribe();
    };
  }, [selectedTimeRange]);

  const fetchDashboardStats = async (timeRange = '7') => {
    try {
      const startDate = getStartDate(timeRange);
      const today = new Date();

      // 1. Fetch tasks within the selected time range
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', today.toISOString());

      if (tasksError) throw tasksError;



      // 2. Fetch active users (users who have logged in within the last 24 hours)
      const { data: users, error: usersError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('online_status', true);

      if (usersError) throw usersError;

      // Calculate task statistics for the selected time range
      const totalTasks = tasks.length;
      const completedTasks = tasks.filter(task => task.status === 'done').length;
      const pendingTasks = tasks.filter(task => task.status === 'todo').length;
      const activeUsers = users.length;

      // Calculate percentage changes (mock data for now - you can implement real historical data comparison)
      const taskStats = {
        totalChange: totalTasks > 0 ? ((totalTasks - (totalTasks * 0.9)) / (totalTasks * 0.9) * 100).toFixed(0) : 0,
        completedChange: completedTasks > 0 ? ((completedTasks - (completedTasks * 0.92)) / (completedTasks * 0.92) * 100).toFixed(0) : 0,
        pendingChange: pendingTasks > 0 ? ((pendingTasks - (pendingTasks * 1.05)) / (pendingTasks * 1.05) * 100).toFixed(0) : 0,
        usersChange: activeUsers > 0 ? ((activeUsers - (activeUsers * 0.8)) / (activeUsers * 0.8) * 100).toFixed(0) : 0
      };

      setStats({
        totalTasks,
        completedTasks,
        pendingTasks,
        activeUsers,
        taskStats
      });

      // Calculate task status statistics for pie chart
      const taskStatusData = [
        { 
          name: 'Tamamlanan', 
          value: tasks.filter(task => task.status === 'done').length,
          fill: '#10B981', 
          color: '#065F46' 
        },
        { 
          name: 'Devam Eden', 
          value: tasks.filter(task => task.status === 'in_progress').length,
          fill: '#3B82F6', 
          color: '#1E40AF' 
        },
        { 
          name: 'Bekleyen', 
          value: tasks.filter(task => task.status === 'todo').length,
          fill: '#F59E0B', 
          color: '#B45309' 
        },
        { 
          name: 'İptal Edilen', 
          value: tasks.filter(task => task.status === 'cancelled').length,
          fill: '#EF4444', 
          color: '#B91C1C' 
        }
      ];

      // Calculate completion rate
      const completedTasksForPie = taskStatusData.find(item => item.name === 'Tamamlanan')?.value || 0;
      const totalTaskCountForPie = taskStatusData.reduce((sum, item) => sum + item.value, 0);
      const completionRateForPie = totalTaskCountForPie > 0 ? ((completedTasksForPie / totalTaskCountForPie) * 100).toFixed(0) : 0;

      setTaskStatusStats({
        pieData: taskStatusData,
        completionRate: completionRateForPie,
        totalTasks: totalTaskCountForPie
      });

      // Set all tasks
      setAllTasks(tasks);

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  // Seçilen zaman aralığına göre başlangıç tarihini hesapla
  const getStartDate = (range) => {
    const today = new Date();
    switch (range) {
      case '0': // Bugün
        return new Date(today.setHours(0, 0, 0, 0));
      case '7': // Son 7 gün
        return new Date(today.setDate(today.getDate() - 7));
      case '30': // Son 30 gün
        return new Date(today.setDate(today.getDate() - 30));
      case '90': // Son 3 ay
        return new Date(today.setDate(today.getDate() - 90));
      case '6': // Son 6 ay
        return new Date(today.setMonth(today.getMonth() - 6));
      case '12': // Son 12 ay
        return new Date(today.setMonth(today.getMonth() - 12));
      default:
        return new Date(today.setMonth(today.getMonth() - 6)); // Varsayılan olarak son 6 ay
    }
  };

  // Görev istatistiklerini getir
  const fetchWeeklyStats = async (timeRange) => {
    try {
      const startDate = getStartDate(timeRange);
      const today = new Date();

      const { data: periodTasks, error: weeklyError } = await supabase
        .from('tasks')
        .select('created_at, status')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', today.toISOString());

      if (weeklyError) throw weeklyError;

      // Zaman aralığına göre veri noktası sayısını belirle
      const dataPoints = timeRange === '0' ? 24 : // Bugün için saatlik
                        timeRange === '7' ? 7 :   // Haftalık için günlük
                        timeRange === '30' ? 30 :  // Aylık için günlük
                        timeRange === '90' ? 90 :  // 3 aylık için günlük
                        timeRange === '6' ? 180 :   // 6 aylık için günlük (yaklaşık)
                        365;                       // 12 aylık için günlük (yaklaşık)

      const periodData = Array.from({ length: dataPoints }, (_, i) => {
        const date = new Date(today);
        if (timeRange === '0') {
          // Bugün için saatlik veriler
          date.setHours(date.getHours() - (dataPoints - 1 - i));
          const hourTasks = periodTasks.filter(task => 
            new Date(task.created_at).getHours() === date.getHours() &&
            new Date(task.created_at).getDate() === date.getDate()
          );

          return {
            name: date.getHours().toString().padStart(2, '0') + ':00',
            tamamlanan: hourTasks.filter(task => task.status === 'done').length,
            bekleyen: hourTasks.filter(task => task.status === 'todo').length,
            devamEden: hourTasks.filter(task => task.status === 'in_progress').length,
          };
        } else {
          // Diğer aralıklar için günlük veriler
          date.setDate(date.getDate() - (dataPoints - 1 - i));
          const dayTasks = periodTasks.filter(task => 
            new Date(task.created_at).toDateString() === date.toDateString()
          );

          return {
            name: timeRange === '7' ? 
              ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'][date.getDay()] :
              date.getDate().toString().padStart(2, '0') + '/' + (date.getMonth() + 1).toString().padStart(2, '0'),
            tamamlanan: dayTasks.filter(task => task.status === 'done').length,
            bekleyen: dayTasks.filter(task => task.status === 'todo').length,
            devamEden: dayTasks.filter(task => task.status === 'in_progress').length,
          };
        }
      });

      setWeeklyStats(periodData);
    } catch (error) {
      console.error('Error fetching period stats:', error);
    }
  };

  // Zaman aralığı değiştiğinde verileri güncelle
  useEffect(() => {
    fetchWeeklyStats(selectedTimeRange);
    fetchDashboardStats(selectedTimeRange);
  }, [selectedTimeRange]);

  const fetchTaskStatusStats = async () => {
    // Implementation needed
  };

  // Ortalama süre hesaplama fonksiyonu:
  const getAverageCompletionTime = () => {
    // Son 7 günün tamamlanan görevleri
    const completed = allTasks.filter(task => task.status === 'done');
    if (completed.length === 0) return 0;
    // Her görev için (tamamlanma tarihi - oluşturulma tarihi)
    const totalMs = completed.reduce((sum, task) => {
      const created = new Date(task.created_at);
      const completedAt = new Date(task.completed_at || task.updated_at || task.end_date || task.created_at);
      return sum + (completedAt - created);
    }, 0);
    return (totalMs / completed.length) / (1000 * 60 * 60 * 24); // gün cinsinden
  };

  // Bugünün özetini hesapla:
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  // Bugün oluşturulan görevler
  const todayTasks = allTasks.filter(task => task.created_at && task.created_at.slice(0, 10) === todayStr);
  // Bugün tamamlanan görevler (tamamlanma tarihi bugün olanlar)
  const todayCompleted = allTasks.filter(task => task.status === 'done' && (task.completed_at ? task.completed_at.slice(0, 10) === todayStr : false));
  // Şu anda devam eden görevler (status: in_progress, bitiş tarihi >= bugün)
  const todayInProgress = allTasks.filter(task => task.status === 'in_progress' && task.end_date && new Date(task.end_date) >= today);
  // Şu anda bekleyen görevler (status: todo, bitiş tarihi >= bugün)
  const todayPending = allTasks.filter(task => task.status === 'todo' && task.end_date && new Date(task.end_date) >= today);
  // Şu anda geciken görevler (status: todo veya in_progress, bitiş tarihi < bugün)
  const todayOverdue = allTasks.filter(task => (task.status === 'todo' || task.status === 'in_progress') && task.end_date && new Date(task.end_date) < today);
  // Bugünün hedefi: bugün oluşturulanlardan kaç tanesi tamamlanmış
  const todayGoal = todayTasks.length > 0 ? Math.round((todayCompleted.length / todayTasks.length) * 100) : 0;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Üst Kartlar */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="dashboard-card bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Toplam Görev
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                      <AnimatedNumber value={stats.totalTasks} />
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stats.taskStats.totalChange >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      <svg className={`self-center flex-shrink-0 h-5 w-5 ${
                        stats.taskStats.totalChange >= 0 
                          ? 'text-green-500 dark:text-green-400' 
                          : 'text-red-500 dark:text-red-400'
                      } ${stats.taskStats.totalChange < 0 ? 'transform rotate-180' : ''}`} 
                        fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="sr-only">{stats.taskStats.totalChange >= 0 ? 'Artış' : 'Azalış'}</span>
                      {Math.abs(stats.taskStats.totalChange)}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Tamamlanan
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                      <AnimatedNumber value={stats.completedTasks} />
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stats.taskStats.completedChange >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      <svg className={`self-center flex-shrink-0 h-5 w-5 ${
                        stats.taskStats.completedChange >= 0 
                          ? 'text-green-500 dark:text-green-400' 
                          : 'text-red-500 dark:text-red-400'
                      } ${stats.taskStats.completedChange < 0 ? 'transform rotate-180' : ''}`} 
                        fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="sr-only">{stats.taskStats.completedChange >= 0 ? 'Artış' : 'Azalış'}</span>
                      {Math.abs(stats.taskStats.completedChange)}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Bekleyen
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                      <AnimatedNumber value={stats.pendingTasks} />
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stats.taskStats.pendingChange <= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      <svg className={`self-center flex-shrink-0 h-5 w-5 ${
                        stats.taskStats.pendingChange <= 0 
                          ? 'text-green-500 dark:text-green-400' 
                          : 'text-red-500 dark:text-red-400'
                      } ${stats.taskStats.pendingChange > 0 ? 'transform rotate-180' : ''}`} 
                        fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="sr-only">{stats.taskStats.pendingChange <= 0 ? 'Azalış' : 'Artış'}</span>
                      {Math.abs(stats.taskStats.pendingChange)}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-card bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-purple-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Çevrimiçi Kullanıcı
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                      <AnimatedNumber value={stats.activeUsers} />
                    </div>
                    <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                      stats.taskStats.usersChange >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      <svg className={`self-center flex-shrink-0 h-5 w-5 ${
                        stats.taskStats.usersChange >= 0 
                          ? 'text-green-500 dark:text-green-400' 
                          : 'text-red-500 dark:text-red-400'
                      } ${stats.taskStats.usersChange < 0 ? 'transform rotate-180' : ''}`} 
                        fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="sr-only">{stats.taskStats.usersChange >= 0 ? 'Artış' : 'Azalış'}</span>
                      {Math.abs(stats.taskStats.usersChange)}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Görev Dağılımı */}
        <div className="chart-card bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Görev Dağılımı
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedTimeRange === '0' ? 'Bugünün' :
                 selectedTimeRange === '7' ? 'Son 7 günün' :
                 selectedTimeRange === '30' ? 'Son 30 günün' :
                 selectedTimeRange === '90' ? 'Son 3 ayın' :
                 selectedTimeRange === '6' ? 'Son 6 ayın' :
                 'Son 12 ayın'} görev istatistikleri
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 dark:text-gray-400">Toplam:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {weeklyStats.reduce((sum, day) => 
                    sum + day.tamamlanan + day.bekleyen + day.devamEden, 0
                  )} görev
                </span>
              </div>
              <select 
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="text-sm border-gray-200 dark:border-gray-700 rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white bg-gray-50"
              >
                <option value="0">Bugün</option>
                <option value="7">Son 7 Gün</option>
                <option value="30">Son 30 Gün</option>
                <option value="90">Son 3 Ay</option>
                <option value="6">Son 6 Ay</option>
                <option value="12">Son 12 Ay</option>
              </select>
            </div>
          </div>

          {/* İstatistik Kartları */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-green-700 dark:text-green-400">Tamamlanan</span>
                <span className="flex items-center text-xs text-green-600 dark:text-green-400">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {((weeklyStats.reduce((sum, day) => sum + day.tamamlanan, 0) / 
                    weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                </span>
              </div>
              <p className="text-2xl font-bold text-green-700 dark:text-green-400 mt-1">
                {weeklyStats.reduce((sum, day) => sum + day.tamamlanan, 0)}
              </p>
            </div>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">Bekleyen</span>
                <span className="flex items-center text-xs text-yellow-600 dark:text-yellow-400">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {((weeklyStats.reduce((sum, day) => sum + day.bekleyen, 0) / 
                    weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                </span>
              </div>
              <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400 mt-1">
                {weeklyStats.reduce((sum, day) => sum + day.bekleyen, 0)}
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700 dark:text-blue-400">Devam Eden</span>
                <span className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {((weeklyStats.reduce((sum, day) => sum + day.devamEden, 0) / 
                    weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1">
                {weeklyStats.reduce((sum, day) => sum + day.devamEden, 0)}
              </p>
            </div>
          </div>

          <div className="h-80">
            {isLoading ? (
              <div className="h-80 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={weeklyStats}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    dx={-10}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                    labelStyle={{ color: '#111827', fontWeight: 600 }}
                    itemStyle={{ color: '#4B5563' }}
                    formatter={(value, name) => [
                      `${value} görev`,
                      name === 'tamamlanan' ? 'Tamamlanan' :
                      name === 'bekleyen' ? 'Bekleyen' : 'Devam Eden'
                    ]}
                    labelFormatter={(label) => `${label} günü`}
                  />
                  <Legend 
                    verticalAlign="top"
                    height={36}
                    iconType="circle"
                    iconSize={8}
                    formatter={(value) => (
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {value === 'tamamlanan' ? 'Tamamlanan' :
                         value === 'bekleyen' ? 'Bekleyen' : 'Devam Eden'}
                      </span>
                    )}
                    wrapperStyle={{
                      paddingBottom: '20px',
                    }}
                  />
                  <Bar 
                    dataKey="tamamlanan" 
                    name="tamamlanan" 
                    fill="#10B981" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    animationBegin={0}
                    animationDuration={1000}
                  >
                    {weeklyStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fillOpacity={0.9} />
                    ))}
                  </Bar>
                  <Bar 
                    dataKey="bekleyen" 
                    name="bekleyen" 
                    fill="#F59E0B" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    animationBegin={200}
                    animationDuration={1000}
                  >
                    {weeklyStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fillOpacity={0.9} />
                    ))}
                  </Bar>
                  <Bar 
                    dataKey="devamEden" 
                    name="devamEden" 
                    fill="#3B82F6" 
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                    animationBegin={400}
                    animationDuration={1000}
                  >
                    {weeklyStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fillOpacity={0.9} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Trend Analizi */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Trend Analizi
            </h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Tamamlanma Oranı</span>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {((weeklyStats.reduce((sum, day) => sum + day.tamamlanan, 0) / 
                      weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div 
                    className="bg-green-500 h-1.5 rounded-full"
                    style={{ 
                      width: `${((weeklyStats.reduce((sum, day) => sum + day.tamamlanan, 0) / 
                        weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100)}%` 
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Bekleme Oranı</span>
                  <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                    {((weeklyStats.reduce((sum, day) => sum + day.bekleyen, 0) / 
                      weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div 
                    className="bg-yellow-500 h-1.5 rounded-full"
                    style={{ 
                      width: `${((weeklyStats.reduce((sum, day) => sum + day.bekleyen, 0) / 
                        weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100)}%` 
                    }}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Devam Eden Oranı</span>
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {((weeklyStats.reduce((sum, day) => sum + day.devamEden, 0) / 
                      weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div 
                    className="bg-blue-500 h-1.5 rounded-full"
                    style={{ 
                      width: `${((weeklyStats.reduce((sum, day) => sum + day.devamEden, 0) / 
                        weeklyStats.reduce((sum, day) => sum + day.tamamlanan + day.bekleyen + day.devamEden, 0)) * 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Görev Durumu */}
        <div className="chart-card bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Görev Durumu
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Görevlerin genel durumu ve dağılımı
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select 
                className="text-sm border-gray-200 dark:border-gray-700 rounded-lg focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white bg-gray-50"
                onChange={(e) => {
                  // Filter logic can be implemented here
                  //console.log('Selected filter:', e.target.value);
                }}
              >
                <option value="all">Tüm Görevler</option>
                <option value="active">Aktif Görevler</option>
                <option value="completed">Tamamlanan</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol Kolon: Pasta Grafik */}
            <div className="h-80 flex items-center justify-center">
              {isLoading ? (
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={taskStatusStats.pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                      animationBegin={0}
                      animationDuration={2000}
                      animationEasing="ease-out"
                      isAnimationActive={true}
                    >
                      {taskStatusStats.pieData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.fill}
                          stroke="none"
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <animate
                            attributeName="opacity"
                            from="0"
                            to="1"
                            dur="1s"
                            begin={`${index * 200}ms`}
                          />
                        </Cell>
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        padding: '12px',
                      }}
                      itemStyle={{ color: '#4B5563' }}
                      formatter={(value, name, props) => [
                        <div className="flex flex-col">
                          <span className="text-lg font-semibold">{value} görev</span>
                          <span className="text-sm text-gray-500">
                            {((value / taskStatusStats.totalTasks) * 100).toFixed(1)}%
                          </span>
                        </div>,
                        <span className="font-medium" style={{ color: props.payload.color }}>{name}</span>
                      ]}
                      animationDuration={200}
                      animationEasing="ease-out"
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Sağ Kolon: Detaylı İstatistikler */}
            <div className="flex flex-col justify-between">
              {/* Durum Kartları */}
              <div className="space-y-4">
                {taskStatusStats.pieData.map((status, index) => (
                  <div 
                    key={status.name}
                    className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: status.fill }} />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {status.name}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {((status.value / taskStatusStats.totalTasks) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${(status.value / taskStatusStats.totalTasks) * 100}%`,
                          backgroundColor: status.fill
                        }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-300">
                        {status.value} görev
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {status.name === 'Tamamlanan' ? 'Başarıyla tamamlandı' :
                         status.name === 'Devam Eden' ? 'Aktif olarak devam ediyor' :
                         status.name === 'Bekleyen' ? 'Başlanmayı bekliyor' :
                         'İptal edildi'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Özet İstatistikler */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-700 dark:text-green-400">
                    <AnimatedNumber value={parseInt(taskStatusStats.completionRate)} />%
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-500">
                    Tamamlanma Oranı
                  </div>
                  <div className="text-xs text-green-500 dark:text-green-600 mt-1">
                    Hedef: 100%
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                    <AnimatedNumber value={taskStatusStats.totalTasks} />
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-500">
                    Toplam Görev
                  </div>
                  <div className="text-xs text-blue-500 dark:text-blue-600 mt-1">
                    Tüm zamanlar
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      

      {/* Son Aktiviteler ve Yaklaşan Görevler */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Son Aktiviteler */}
        <div className="chart-card bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Son Aktiviteler
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Sistemdeki son değişiklikler ve güncellemeler
                </p>
              </div>
            </div>
            <div className="flow-root">
              {activities.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-base">Henüz aktivite bulunmuyor</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Yeni aktiviteler burada görüntülenecek</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.slice(0, showAllActivities ? 4 : 2).map((activity, index, arr) => (
                    <div 
                      key={activity.id}
                      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 transition-all duration-200 hover:shadow-lg hover:border-primary-100 dark:hover:border-primary-800 relative ${
                        !showAllActivities && index === arr.length - 1 ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-16 after:bg-gradient-to-b after:from-transparent after:to-white dark:after:to-gray-800 after:rounded-b-xl' : ''
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Kullanıcı Avatarı */}
                        <div className="flex-shrink-0">
                          {activity.user.avatar ? (
                            <img
                              src={activity.user.avatar}
                              alt={activity.user.name}
                              className="h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center ring-2 ring-white dark:ring-gray-800">
                              <span className="text-white font-medium text-lg">
                                {activity.user.name.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Aktivite İçeriği */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {activity.user.name}
                              </p>
                              {activity.user.position && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {activity.user.position}
                                </p>
                              )}
                            </div>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              activity.type === 'create' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                              activity.type === 'update' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {activity.type === 'create' ? 'Yeni' :
                               activity.type === 'update' ? 'Güncelleme' : 'Silindi'}
                            </span>
                          </div>

                          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {activity.description}
                          </p>

                          {activity.taskTitle && activity.task_id && (
                            <Link 
                              to={`/tasks?taskId=${activity.task_id}`}
                              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              {activity.taskTitle}
                            </Link>
                          )}

                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formatDistanceToNow(activity.createdAt, { addSuffix: true, locale: tr })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {activities.length > 2 && (
                    <button
                      onClick={() => setShowAllActivities(!showAllActivities)}
                      className="w-full mt-4 px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors focus:outline-none"
                    >
                      {showAllActivities ? 'Daha Az Göster' : 'Daha Fazla Göster'}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="mt-6">
              <Link
                to="/activities"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <svg className="mr-2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Tüm Aktiviteleri Görüntüle
              </Link>
            </div>
          </div>
        </div>

        {/* Yaklaşan Görevler */}
        <div className="chart-card bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Yaklaşan Görevler
            </h3>
            <div className="overflow-hidden">
              {upcomingTasks.length > 0 ? (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {upcomingTasks.map((task) => (
                    <li key={task.id} className="py-4">
                      <Link
                        to={`/tasks?taskId=${task.id}`}
                        className="flex items-center space-x-4 group"
                      >
                        <div className="flex-shrink-0">
                          <span className={`h-8 w-8 rounded-full bg-${task.status.color}-100 dark:bg-${task.status.color}-900/30 text-${task.status.color}-600 dark:text-${task.status.color}-400 flex items-center justify-center`}>
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {task.title}
                          </p>
                          <div className="mt-1">
                            {task.project?.name && (
                              <span className="inline-flex items-center mr-3 text-xs text-gray-500 dark:text-gray-400">
                                <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                </svg>
                                {task.project.name}
                              </span>
                            )}
                            <span className={`inline-flex items-center text-xs text-${task.status.color}-600 dark:text-${task.status.color}-400`}>
                              <svg className="mr-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {task.status.message}
                            </span>
                          </div>
                          {task.status.status !== 'upcoming' && (
                            <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                              <div 
                                className={`h-1.5 rounded-full bg-${task.status.color}-500 transition-all duration-500`}
                                style={{ width: `${task.status.progress}%` }}
                              />
                            </div>
                          )}
                          
                          {/* Ekip Üyeleri Avatarları */}
                          {task.assigned_members && task.assigned_members.length > 0 && (
                            <div className="mt-3 flex -space-x-2 overflow-hidden">
                              {task.assigned_members.map((member, index) => (
                                <img
                                  key={member.id}
                                  src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3b82f6&color=fff`}
                                  alt={member.name}
                                  className="inline-block h-6 w-6 rounded-full ring-2 ring-white dark:ring-gray-800"
                                  title={member.name}
                                />
                              ))}
                              {task.assigned_members.length > 3 && (
                                <span className="flex items-center justify-center h-6 w-6 rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-100 dark:bg-gray-700">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    +{task.assigned_members.length - 3}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getPriorityColor(task.priority)}-100 dark:bg-${getPriorityColor(task.priority)}-900/30 text-${getPriorityColor(task.priority)}-800 dark:text-${getPriorityColor(task.priority)}-400`}>
                            {task.priority === 'high' ? 'Acil' : task.priority === 'medium' ? 'Orta' : 'Normal'}
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-6">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Yaklaşan görev yok</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Aktif görev bulunmuyor.</p>
                </div>
              )}
              <div className="mt-6">
                <Link
                  to="/tasks"
                  className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Tüm Görevleri Görüntüle
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Yeni Özellikler Bölümü */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Hızlı İstatistikler */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Hızlı İstatistikler
            </h3>
            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Bu Hafta</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tamamlanan görevler</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  {weeklyStats.slice(-7).reduce((sum, day) => sum + day.tamamlanan, 0)}
                </p>
                <p className="text-xs text-green-500 dark:text-green-500">+12%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Ortalama Süre</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Görev tamamlama</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{getAverageCompletionTime().toFixed(1)} gün</p>
                <p className="text-xs text-blue-500 dark:text-blue-500">-8%</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Aktif Kullanıcı</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Şu anda çevrimiçi</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.activeUsers}</p>
                <p className="text-xs text-purple-500 dark:text-purple-500">+5%</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hızlı Eylemler */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Hızlı Eylemler
            </h3>
            <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          
          <div className="space-y-3">
            <Link
              to="/tasks/create"
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-lg hover:from-primary-100 hover:to-blue-100 dark:hover:from-primary-800/30 dark:hover:to-blue-800/30 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Yeni Görev</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Hızlı görev oluştur</p>
              </div>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            <Link
              to="/calendar"
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg hover:from-green-100 hover:to-emerald-100 dark:hover:from-green-800/30 dark:hover:to-emerald-800/30 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Takvim Görünümü</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Görevleri planla</p>
              </div>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-green-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            <Link
              to="/projects"
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg hover:from-purple-100 hover:to-pink-100 dark:hover:from-purple-800/30 dark:hover:to-pink-800/30 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Projeler</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Proje yönetimi</p>
              </div>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            <Link
              to="/team"
              className="flex items-center gap-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg hover:from-yellow-100 hover:to-orange-100 dark:hover:from-yellow-800/30 dark:hover:to-orange-800/30 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Ekip Yönetimi</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Ekip üyelerini yönet</p>
              </div>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-yellow-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Günlük Özet */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Günlük Özet
            </h3>
            <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">Bugünün Hedefleri</h4>
                <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{todayGoal}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${todayGoal}%` }}></div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{todayCompleted.length}/{todayTasks.length} görev tamamlandı</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Yeni görevler</span>
                <span className="font-medium text-gray-900 dark:text-white">{todayTasks.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Tamamlanan</span>
                <span className="font-medium text-green-600 dark:text-green-400">{todayCompleted.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Bekleyen</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">{todayPending.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Devam Eden</span>
                <span className="font-medium text-blue-600 dark:text-blue-400">{todayInProgress.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Geciken</span>
                <span className="font-medium text-red-600 dark:text-red-400">{todayOverdue.length}</span>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Son güncelleme: {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  )
}

function App() {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null); // currentUser state'i eklendi
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    try {
      const settings = localStorage.getItem('notificationSettings');
      return settings ? JSON.parse(settings).sound : true;
    } catch {
      return true;
    }
  });
  
  // Bildirim ses ayarlarını veritabanından çeken fonksiyon
  const fetchNotificationSoundPreference = async (userId) => {
    if (!userId) return;
    try {
      const { data: preferences, error } = await supabase
        .from('notification_preferences')
        .select('notification_sound_enabled')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching sound preferences:', error);
        return;
      }

      if (!preferences) {
        console.log('No notification preferences found, creating default settings');
        const defaultSoundEnabled = true;
        
        const { error: insertError } = await supabase
          .from('notification_preferences')
          .insert({
            user_id: userId,
            notification_sound_enabled: defaultSoundEnabled,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Error creating default sound preferences:', insertError);
          return;
        }

        setIsSoundEnabled(defaultSoundEnabled);
        updateNotificationSoundSettings(defaultSoundEnabled);
        return;
      }

      setIsSoundEnabled(preferences.notification_sound_enabled);
      updateNotificationSoundSettings(preferences.notification_sound_enabled);
    } catch (error) {
      console.error('Error fetching sound preferences:', error);
    }
  };

  // Ses ayarını değiştirme fonksiyonu
  const toggleSound = async () => {
    if (!currentUser) return;
    try {
      const newSoundEnabled = !isSoundEnabled;
      const timestamp = new Date().toISOString();

      const { data: existingPreference, error: fetchError } = await supabase
        .from('notification_preferences')
        .select('id')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (fetchError) {
        console.error('Error checking preference existence:', fetchError);
        return;
      }

      let error;
      
      if (existingPreference) {
        const result = await supabase
          .from('notification_preferences')
          .update({ 
            notification_sound_enabled: newSoundEnabled,
            updated_at: timestamp
          })
          .eq('user_id', currentUser.id);
        
        error = result.error;
      } else {
        const result = await supabase
          .from('notification_preferences')
          .insert({
            user_id: currentUser.id,
            notification_sound_enabled: newSoundEnabled,
            created_at: timestamp,
            updated_at: timestamp
          });
        
        error = result.error;
      }

      if (error) throw error;

      setIsSoundEnabled(newSoundEnabled);
      updateNotificationSoundSettings(newSoundEnabled);
      
    } catch (error) {
      console.error('Error updating sound preference:', error);
    }
  };

  useEffect(() => {
    // Check active sessions and sets the user
    const initializeAuth = async () => {
      try {
        // Timeout ekle (8 saniye)
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('App auth timeout')), 8000)
        );
        
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]);
        
        if (error) {
          console.warn('App session check error:', error);
          // Hata durumunda session'ı null olarak ayarla
          setSession(null);
        } else {
          setSession(session);
          setCurrentUser(session?.user || null);
          if (session?.user) {
            fetchNotificationSoundPreference(session.user.id);
          }
        }

        // Oturum değişikliklerini dinle
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
          setSession(session);
          setCurrentUser(session?.user || null); // currentUser'ı güncelle
          if (session?.user) {
            fetchNotificationSoundPreference(session.user.id);
          }
          
          // Oturum kapandığında local storage'ı temizle
          if (!session) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('userProfile');
          }
        });

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Initialization hata durumunda session'ı null olarak ayarla
        setSession(null);
      } finally {
        // Her durumda loading'i false yap
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Realtime notification subscription
  useEffect(() => {
    if (!currentUser) return;

    const notificationsSubscription = supabase
      .channel('global_notifications_channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          //console.log('Global notification received:', payload);
          
          if (isSoundEnabled) {
            playNotificationSound();
          }
        }
      )
      .subscribe();

    // Component unmount olduğunda aboneliği iptal et
    return () => {
      supabase.removeChannel(notificationsSubscription);
    };
  }, [currentUser, isSoundEnabled]);

  useEffect(() => {
    // Smooth scroll özelliklerini başlat
    const lenisInstance = initSmoothScrolling();
    
    // Kaydırılabilir alanları tespit et ve Lenis'in bu alanları etkilememesini sağla
    const scrollHelperCleanup = initScrollHelpers();
    
    return () => {
      // Component unmount olduğunda kaynakları temizle
      if (lenisInstance) {
        lenisInstance.destroy();
      }
      
      if (scrollHelperCleanup) {
        scrollHelperCleanup();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <LoadingFallback 
        message="Uygulama başlatılıyor..."
        timeout={10000}
        onTimeout={(isRetry) => {
          if (isRetry) {
            // Retry durumunda initialization'ı yeniden çalıştır
            setIsLoading(true);
            const initializeAuth = async () => {
              try {
                // Timeout ekle (8 saniye)
                const sessionPromise = supabase.auth.getSession();
                const timeoutPromise = new Promise((_, reject) => 
                  setTimeout(() => reject(new Error('App auth timeout')), 8000)
                );
                
                const { data: { session }, error } = await Promise.race([
                  sessionPromise,
                  timeoutPromise
                ]);
                
                if (error) {
                  console.warn('App session check error:', error);
                  // Hata durumunda session'ı null olarak ayarla
                  setSession(null);
                } else {
                  setSession(session);
                }

                // Oturum değişikliklerini dinle
                const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
                  setSession(session);
                  
                  // Oturum kapandığında local storage'ı temizle
                  if (!session) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    localStorage.removeItem('userProfile');
                  }
                });

                return () => subscription.unsubscribe();
              } catch (error) {
                console.error('Auth initialization error:', error);
                // Initialization hata durumunda session'ı null olarak ayarla
                setSession(null);
              } finally {
                // Her durumda loading'i false yap
                setIsLoading(false);
              }
            };
            initializeAuth();
          }
        }}
      />
    );
  }

  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={<Navigate to="/welcome" />}
            />
            <Route path="/welcome" element={<LandingPage />} />
            <Route path="/signin" element={!session ? <LoginForm /> : (
              localStorage.getItem('activePanel') === 'stock' 
                ? <Navigate to="/stokflow" /> 
                : <Navigate to="/dashboard" />
            )} />
            <Route path="/register" element={!session ? <RegisterForm /> : (
              localStorage.getItem('activePanel') === 'stock' 
                ? <Navigate to="/stokflow" /> 
                : <Navigate to="/dashboard" />
            )} />
            <Route path="/forgot-password" element={!session ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/reset-password" element={!session ? <ResetPassword /> : <Navigate to="/dashboard" />} />
            <Route path="/verify-email" element={!session ? <EmailVerification /> : <Navigate to="/dashboard" />} />
            {/* Test route - remove in production */}
            <Route path="/test-404" element={<TestNotFound />} />
            <Route path="/test-scroll" element={<TestScroll />} />

            {/* Protected Routes */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/tasks/create" element={<CreateTask />} />
              <Route path="/notifications" element={<Notifications />} />
              {/* Ortak bileşenler için PanelModePreserver eklendi */}
              <Route path="/team" element={<PanelModePreserver component={Team} />} />
              <Route path="/files" element={<PanelModePreserver component={Files} />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/chat" element={<PanelModePreserver component={Chat} />} />
              <Route path="/consulting" element={<Consulting />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/notification-preferences" element={<NotificationPreferences />} />
              {/* Admin Routes */}
              <Route path="/admin/data-integrity" element={<DataIntegrityCheck />} />
              {/* <Route path="/help-support" element={<HelpSupport />} /> */}
            </Route>

            {/* Stok Takip Routes */}
            <Route
              path="/stokflow"
              element={
                <ProtectedRoute>
                  <StokFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stokflow/products/create"
              element={
                <ProtectedRoute>
                  <StokProductCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stokflow/product/edit/:productId"
              element={
                <ProtectedRoute>
                  <StokProductEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stokflow/products"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <StokYonetimi />
                  </StokLayout>
                </ProtectedRoute>
              }
            />
            {/* Add Transactions Route */}
            <Route
              path="/stokflow/transactions"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <Transactions />
                  </StokLayout>
                </ProtectedRoute>
              }
            />

            {/* Stok Takip sistemi için ortak menüler */}
            <Route
              path="/stokflow/files"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <Files />
                  </StokLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stokflow/team"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <Team />
                  </StokLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stokflow/chat"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <Chat />
                  </StokLayout>
                </ProtectedRoute>
              }
            />

            {/* Stok Yönetimi Routes */}
            <Route
              path="/stokflow/products"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    {/* Stok Yönetimi Komponenti (Placeholder) */}
                    <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow">
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Stok Yönetimi</h1>
                      <p className="text-gray-600 dark:text-gray-300">
                        Bu bölümde stok ve ürün yönetimi işlemlerini gerçekleştirebilirsiniz.
                      </p>
                    </div>
                  </StokLayout>
                </ProtectedRoute>
              }
            />

            {/* Makine Montaj Listesi Routes */}
            <Route
              path="/stokflow/catalog"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    <MakineMontajListesi />
                  </StokLayout>
                </ProtectedRoute>
              }
            />

            {/* İşlem Geçmişi Routes */}
            <Route
              path="/stokflow/transactions"
              element={
                <ProtectedRoute>
                  <StokLayout>
                    {/* İşlem Geçmişi Komponenti (Placeholder) */}
                    <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow">
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">İşlem Geçmişi</h1>
                      <p className="text-gray-600 dark:text-gray-300">
                        Bu bölümde stok giriş-çıkış işlem geçmişini görüntüleyebilirsiniz.
                      </p>
                    </div>
                  </StokLayout>
                </ProtectedRoute>
              }
            />

            {/* Profile Routes */}
            <Route
              path="/profile-info"
              element={
                <ProtectedRoute>
                  <ProfileInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route 
              path="/security-privacy" 
              element={
                <ProtectedRoute>
                  <SecurityPrivacy />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/theme-preferences" 
              element={
                <ProtectedRoute>
                  <ThemePreferences />
                </ProtectedRoute>
              } 
            />
            
            {/* Contact Route */}
            <Route path="/contact" element={<Contact />} />
            
            {/* Help Support route - now publicly accessible */}
            <Route path="/help-support" element={<HelpSupport />} />
            
            {/* 404 Not Found Route - Will catch all unmatched routes */}
            <Route path="*" element={<NotFound />} />

            {/* Add these routes with the other route definitions */}
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/tutorials" element={<Tutorials />} />
            {/* Blog route */}
            <Route path="/blog" element={<Blog />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/kvkk" element={<KVKK />} />
            
            {/* Help Support route - protected with authentication check */}
            {/* <Route path="/help-support" element={
              <ProtectedRoute>
                <HelpSupport />
              </ProtectedRoute>
            } /> */}
            <Route path="/demo" element={<Demo />} />
          </Routes>
          
          {/* Cookie Consent Banner - appears on all pages */}
          <CookieConsent />
        </Router>
        
        {/* Cookie Consent Banner - appears on all pages */}
        <CookieConsent />
      </ThemeProvider>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App 